import { createTheme } from '@mui/material/styles';

const colors = {
    background: '#0d1117',
    surface: '#161b22',
    surfaceRaised: '#1c2128',
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textSoft: '#c9d1d9',
    textMuted: '#484f58',
    primary: '#4CC9F0',
    primaryLight: '#7ED8F7',
    primaryDark: '#0096C7',
    secondary: '#F72585',
    secondaryLight: '#FA6EAB',
    secondaryDark: '#C0006A',
    success: '#06D6A0',
    warning: '#FFD166',
    error: '#EF233C',
    errorBright: '#ef4444',
    info: '#4CC9F0',
    orange: '#FF9F1C',
    purple: '#7209B7',
    violet: '#7c3aed',
    soloPurple: '#c77dff',
    wordleGreen: '#6aaa64',
    wordleGreenDark: '#4a7a42',
    blue: '#3A86FF',
    quizOrange: '#FB8500',
    quizGold: '#FFB703',
    quizPurple: '#8338EC',
    quizPink: '#FF006E',
    quizRedOrange: '#FB5607',
    quizGreen: '#2DC653',
    ludoBlue: '#4361EE',
    unoPurple: '#a855f7',
    unoRed: '#DC2626',
    ludoPink: '#EF476F',
    borderSubtle: 'rgba(255,255,255,0.08)',
    borderSoft: 'rgba(255,255,255,0.05)',
    borderStrong: 'rgba(255,255,255,0.15)',
    borderFaint: 'rgba(255,255,255,0.03)',
    borderLight: 'rgba(255,255,255,0.06)',
    borderMedium: 'rgba(255,255,255,0.07)',
    borderHover: 'rgba(255,255,255,0.1)',
    borderEmphasis: 'rgba(255,255,255,0.12)',
    borderFocus: 'rgba(255,255,255,0.2)',
    primaryFieldHover: 'rgba(76,201,240,0.5)',
    scrollbar: '#30363d',
    black: '#000000',
    white: '#ffffff',
};

export const designTokens = {
  colors,
  fonts: {
    body: '"Nunito", "Fredoka One", sans-serif',
    display: '"Fredoka One", cursive',
    monospace: 'monospace',
  },
  radii: {
    small: '8px',
    control: '12px',
    standard: '14px',
    card: '20px',
    pill: '999px',
  },
  shadows: {
    card: '0 8px 32px rgba(0,0,0,0.4)',
    primaryGlow: '0 4px 20px rgba(76,201,240,0.3)',
  },
  avatarColors: ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', colors.secondary, colors.purple, '#3A0CA3', colors.ludoBlue, colors.primary, colors.success],
  games: {
    botColors: ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#06b6d4', '#84cc16'],
    drawingPalette: [
      '#000000', '#374151', '#6B7280', '#FFFFFF',
      '#EF4444', '#F97316', '#EAB308', '#22C55E',
      '#06D6A0', '#3B82F6', '#6366F1', '#8B5CF6',
      '#EC4899', '#F43F5E', '#0EA5E9', '#14B8A6',
      '#84CC16', '#A855F7', '#F59E0B', '#8B4513',
    ],
    rankMedals: ['#FFD700', '#C0C0C0', '#CD7F32'],
    glows: {
      drawing: colors.primary,
      ludo: colors.warning,
      snakeladder: colors.success,
      uno: colors.unoPurple,
      boggle: colors.success,
      quiz: colors.primary,
    },
    gradients: {
      drawing: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.purple} 100%)`,
      ludo: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.ludoPink} 100%)`,
      snakeladder: `linear-gradient(135deg, ${colors.success} 0%, #118AB2 100%)`,
      uno: `linear-gradient(135deg, ${colors.unoRed} 0%, ${colors.violet} 100%)`,
      boggle: `linear-gradient(135deg, ${colors.success} 0%, ${colors.blue} 100%)`,
      quiz: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.success} 100%)`,
      solo: `linear-gradient(135deg, ${colors.success} 0%, ${colors.primary} 100%)`,
    },
    ludoPlayers: {
      red: { hex: colors.error, light: '#FFEEF0', dark: '#B71C1C', name: 'Red' },
      blue: { hex: colors.ludoBlue, light: '#EEF1FF', dark: '#1A237E', name: 'Blue' },
      green: { hex: colors.success, light: '#E8FDF7', dark: '#00695C', name: 'Green' },
      yellow: { hex: colors.warning, light: '#FFFBE6', dark: '#E65100', name: 'Yellow' },
    },
    quizTopics: {
      general: colors.primary,
      science: colors.success,
      history: colors.quizGold,
      geography: colors.quizOrange,
      sports: colors.quizPurple,
      movies: colors.quizPink,
      music: colors.blue,
      technology: colors.purple,
      food: colors.quizRedOrange,
      nature: colors.quizGreen,
    },
    quizOptions: [colors.blue, colors.quizPink, colors.quizRedOrange, colors.success],
    snakeLadder: {
      players: [
        { id: 'red', hex: '#EF4444', light: '#FEE2E2', name: 'Red', emoji: '🔴' },
        { id: 'blue', hex: '#3B82F6', light: '#DBEAFE', name: 'Blue', emoji: '🔵' },
        { id: 'green', hex: '#22C55E', light: '#DCFCE7', name: 'Green', emoji: '🟢' },
        { id: 'yellow', hex: '#EAB308', light: '#FEF9C3', name: 'Yellow', emoji: '🟡' },
        { id: 'purple', hex: '#A855F7', light: '#F3E8FF', name: 'Purple', emoji: '🟣' },
        { id: 'orange', hex: '#F97316', light: '#FFEDD5', name: 'Orange', emoji: '🟠' },
        { id: 'pink', hex: '#EC4899', light: '#FCE7F3', name: 'Pink', emoji: '🩷' },
        { id: 'cyan', hex: '#06B6D4', light: '#CFFAFE', name: 'Cyan', emoji: '🩵' },
        { id: 'lime', hex: '#84CC16', light: '#ECFCCB', name: 'Lime', emoji: '🍏' },
        { id: 'teal', hex: '#14B8A6', light: '#CCFBF1', name: 'Teal', emoji: '🌊' },
        { id: 'rose', hex: '#FB7185', light: '#FFE4E6', name: 'Rose', emoji: '🌹' },
        { id: 'indigo', hex: '#6366F1', light: '#E0E7FF', name: 'Indigo', emoji: '💜' },
      ],
      snakeColors: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#06b6d4'],
      ladderColors: ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#14b8a6', '#f97316', '#84cc16', '#06b6d4'],
      board: {
        darkCells: ['#0e1520', '#111a28'],
        snakeHeadBackground: '#2d0a0a',
        snakeHeadBorder: '#7f1d1d',
        ladderBaseBackground: '#0a1f0e',
        ladderBaseBorder: '#14532d',
        goalBackground: '#1a1400',
      },
    },
    unoColors: {
      red: { hex: '#E53935', dark: '#B71C1C', name: 'Red', emoji: '🔴' },
      blue: { hex: '#1E88E5', dark: '#0D47A1', name: 'Blue', emoji: '🔵' },
      green: { hex: '#43A047', dark: '#1B5E20', name: 'Green', emoji: '🟢' },
      yellow: { hex: '#FDD835', dark: '#F57F17', name: 'Yellow', emoji: '🟡' },
    },
    minigolf: {
      holeBackgrounds: ['#1e4d20', '#1a3d4d', '#2d1f50', '#4d2a1a', '#101025'],
      ballColors: ['#FFFFFF', '#FACC15', '#FB923C', '#F472B6', '#34D399', '#60A5FA', '#A78BFA', '#F87171'],
      wallColor: '#5d4037',
    },
    tetrisPieces: {
      I: colors.primary,
      O: colors.warning,
      T: colors.soloPurple,
      S: colors.success,
      Z: '#EF476F',
      J: colors.ludoBlue,
      L: colors.orange,
    },
    soloThemes: {
      racing: { color: colors.orange, gradient: `linear-gradient(135deg, ${colors.orange} 0%, ${colors.ludoPink} 100%)` },
      tetris: { color: colors.soloPurple, gradient: `linear-gradient(135deg, ${colors.soloPurple} 0%, ${colors.purple} 100%)` },
      flappy: { color: colors.success, gradient: `linear-gradient(135deg, ${colors.success} 0%, ${colors.primary} 100%)` },
      hangman: { color: colors.soloPurple, gradient: `linear-gradient(135deg, ${colors.soloPurple} 0%, ${colors.purple} 100%)` },
      wordle: { color: colors.wordleGreen, gradient: `linear-gradient(135deg, ${colors.wordleGreen} 0%, ${colors.wordleGreenDark} 100%)` },
    },
  },
};

const { fonts, radii, shadows } = designTokens;
const cssVariables = {
    '--app-background': colors.background,
    '--app-surface': colors.surface,
    '--app-surface-raised': colors.surfaceRaised,
    '--app-text-primary': colors.textPrimary,
    '--app-text-secondary': colors.textSecondary,
    '--app-text-soft': colors.textSoft,
    '--app-text-muted': colors.textMuted,
    '--app-primary': colors.primary,
    '--app-primary-light': colors.primaryLight,
    '--app-primary-dark': colors.primaryDark,
    '--app-secondary': colors.secondary,
    '--app-secondary-light': colors.secondaryLight,
    '--app-secondary-dark': colors.secondaryDark,
    '--app-success': colors.success,
    '--app-warning': colors.warning,
    '--app-error': colors.error,
    '--app-error-bright': colors.errorBright,
    '--app-info': colors.info,
    '--app-border-subtle': colors.borderSubtle,
    '--app-border-soft': colors.borderSoft,
    '--app-border-strong': colors.borderStrong,
    '--app-border-faint': colors.borderFaint,
    '--app-border-light': colors.borderLight,
    '--app-border-medium': colors.borderMedium,
    '--app-border-hover': colors.borderHover,
    '--app-border-emphasis': colors.borderEmphasis,
    '--app-border-focus': colors.borderFocus,
    '--app-primary-field-hover': colors.primaryFieldHover,
    '--app-scrollbar': colors.scrollbar,
    '--app-black': colors.black,
    '--app-white': colors.white,
    '--font-body': fonts.body,
    '--font-display': fonts.display,
    '--font-monospace': fonts.monospace,
    '--radius-small': radii.small,
    '--radius-control': radii.control,
    '--radius-standard': radii.standard,
    '--radius-card': radii.card,
    '--radius-pill': radii.pill,
    '--shadow-card': shadows.card,
    '--shadow-primary-glow': shadows.primaryGlow,
};

export const GAME_GLOW = designTokens.games.glows;
export const GAME_GRADIENTS = designTokens.games.gradients;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: colors.primary, light: colors.primaryLight, dark: colors.primaryDark },
    secondary: { main: colors.secondary, light: colors.secondaryLight, dark: colors.secondaryDark },
    success: { main: colors.success },
    warning: { main: colors.warning },
    error: { main: colors.error },
    info: { main: colors.info },
    background: { default: colors.background, paper: colors.surface },
    text: { primary: colors.textPrimary, secondary: colors.textSecondary, disabled: colors.textMuted },
    divider: colors.borderSubtle,
  },
  typography: {
    fontFamily: fonts.body,
    h1: { fontWeight: 800, letterSpacing: '-1px' },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.3px' },
  },
  shape: { borderRadius: radii.standard },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': cssVariables,
        body: {
          background: colors.background,
          color: colors.textPrimary,
          minHeight: '100dvh',
          scrollbarWidth: 'thin',
          scrollbarColor: `${colors.scrollbar} ${colors.background}`,
          fontFamily: fonts.body,
        },
        '::-webkit-scrollbar': { width: '6px' },
        '::-webkit-scrollbar-track': { background: colors.background },
        '::-webkit-scrollbar-thumb': { background: colors.scrollbar, borderRadius: radii.small },
        'button, [role="button"], a': { touchAction: 'manipulation' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { background: colors.surface, borderBottom: `1px solid ${colors.borderSubtle}`, boxShadow: 'none' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', background: colors.surface, border: `1px solid ${colors.borderSubtle}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: colors.surface,
          border: `1px solid ${colors.borderSubtle}`,
          borderRadius: radii.card,
          boxShadow: shadows.card,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.control,
          padding: '10px 24px',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': { boxShadow: shadows.primaryGlow, transform: 'translateY(-1px)' },
          transition: 'all 0.2s ease',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          color: colors.background,
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryLight} 100%)`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radii.control,
            background: colors.background,
            '& fieldset': { borderColor: colors.borderStrong },
            '&:hover fieldset': { borderColor: colors.primaryFieldHover },
            '&.Mui-focused fieldset': { borderColor: colors.primary },
          },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: radii.small, fontWeight: 700 } } },
    MuiDivider: { styleOverrides: { root: { borderColor: colors.borderSubtle } } },
  },
});
